import {
  CM_TO_IN,
  CredGridFormat,
  CredGridLayout,
} from "./layoutTypes";

const cm = ({ value }: { value: number }) => value * CM_TO_IN;

export const SUPPORTED_CRED_GRID_ROWS = [3, 4, 5] as const;
export const SUPPORTED_CRED_GRID_COLUMNS = [3, 4, 5, 6, 7] as const;

// RRINT-3001 derives row gaps from full-grid reservations. The June sample gaps and
// A/B top positions already included an outer-space adjustment and are not input limits.
export const CRED_GRID_LAYOUTS: Record<CredGridFormat, CredGridLayout> = {
  A: {
    format: "A",
    rows: 3,
    columns: 3,
    labelFontSizePt: 12,
    logoLabelGapIn: 0.08,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 2,
    spacingLabelLines: 4,
    outerSlackFraction: 0.2,
    targetLogoHeightIn: cm({ value: 1.70 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
  B: {
    format: "B",
    rows: 3,
    columns: 4,
    labelFontSizePt: 12,
    logoLabelGapIn: 0.08,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 2,
    spacingLabelLines: 4,
    outerSlackFraction: 0.2,
    targetLogoHeightIn: cm({ value: 1.65 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
  C: {
    format: "C",
    rows: 4,
    columns: 4,
    labelFontSizePt: 12,
    logoLabelGapIn: 0.08,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 2,
    spacingLabelLines: 4,
    outerSlackFraction: 0,
    targetLogoHeightIn: cm({ value: 1.55 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
  D: {
    format: "D",
    rows: 4,
    columns: 5,
    labelFontSizePt: 12,
    logoLabelGapIn: 0.08,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 2,
    spacingLabelLines: 4,
    outerSlackFraction: 0,
    targetLogoHeightIn: cm({ value: 1.55 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
  E: {
    format: "E",
    rows: 5,
    columns: 5,
    labelFontSizePt: 12,
    logoLabelGapIn: 0.03,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 2,
    spacingLabelLines: 4,
    outerSlackFraction: 0,
    targetLogoHeightIn: cm({ value: 1.40 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
  F: {
    format: "F",
    rows: 5,
    columns: 6,
    labelFontSizePt: 11,
    logoLabelGapIn: 0.03,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 2,
    spacingLabelLines: 4,
    outerSlackFraction: 0,
    targetLogoHeightIn: cm({ value: 1.40 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
  G: {
    format: "G",
    rows: 5,
    columns: 7,
    labelFontSizePt: 11,
    logoLabelGapIn: 0.03,
    maxLogoWidthRatio: 0.92,
    maxLabelLines: 3,
    spacingLabelLines: 4,
    outerSlackFraction: 0,
    targetLogoHeightIn: cm({ value: 1.35 }),
    columnGapIn: cm({ value: 0.38 }),
    topContentStartIn: cm({ value: 3.52 }),
    sideMarginIn: cm({ value: 1.4 }),
  },
};

export const resolveCredGridFormat = ({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}): CredGridFormat => {
  const match = Object.values(CRED_GRID_LAYOUTS).find((layout) => (
    layout.rows === rows && layout.columns === columns
  ));

  if (!match) {
    throw new Error(`Unsupported credential grid format: ${rows} rows x ${columns} columns`);
  }

  return match.format;
};

export const isSupportedCredGridPair = ({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}): boolean => {
  return Object.values(CRED_GRID_LAYOUTS).some((layout) => (
    layout.rows === rows && layout.columns === columns
  ));
};

export const getValidColumnOptions = ({ rows }: { rows: number }): number[] => {
  return Object.values(CRED_GRID_LAYOUTS)
    .filter((layout) => layout.rows === rows)
    .map((layout) => layout.columns);
};

export const getClosestValidColumn = ({
  rows,
  preferredColumns,
}: {
  rows: number;
  preferredColumns: number;
}): number => {
  const validColumns = getValidColumnOptions({ rows });
  if (!validColumns.length) {
    throw new Error(`Unsupported credential grid row count: ${rows}`);
  }

  return validColumns.reduce((closestColumn, column) => {
    const currentDistance = Math.abs(column - preferredColumns);
    const closestDistance = Math.abs(closestColumn - preferredColumns);

    if (currentDistance === closestDistance) {
      return Math.max(column, closestColumn);
    }

    return currentDistance < closestDistance ? column : closestColumn;
  }, validColumns[0]);
};
